import React, { useMemo } from 'react';
import idConfig from 'utils/ecommerce/services/id.config';
import SummaryRow from 'components/SummaryRow/SummaryRow';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Business from '@material-ui/icons/Business';
import { translate } from 'components/Translation';
import { serviceType } from 'views/AddNewServices/AddSingleService/constants';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/addLineDialogStyle';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import VatClause from 'components/VatClause/VatClause';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ISummaryTabProps {
	account: IAlternativeSelectItem | null;
	selectedBillingAccount: types.newServices.IBillingId | null;
	selectedNumber: string;
	tariffPlan: types.newServices.IOrderItem | null;
	creditLimit: string;
	selectedOrderItems: types.newServices.IOrderItem[];
	selectedUser: SingleUser | null;
	availableServices: number[];
	commitmentDuration: string | null;
	isRoamingInSelectedTariff: boolean | undefined;
}

const SummaryTab = ({
	account,
	selectedBillingAccount,
	selectedNumber,
	tariffPlan,
	creditLimit,
	selectedOrderItems,
	selectedUser,
	availableServices,
	commitmentDuration,
	isRoamingInSelectedTariff,
}: ISummaryTabProps) => {
	const classes = useStyles();

	const creditLimitPickerVisible = availableServices.indexOf(serviceType.CREDIT_LIMIT) >= 0;
	const mobileInternetPlanPickerVisible = availableServices.indexOf(serviceType.MOBILE_INTERNET_PLANS) >= 0;
	const gsmServicesVisible = availableServices.indexOf(serviceType.GSM) >= 0;

	const gsmServices = useMemo(() => {
		const _selectedOrderItems = selectedOrderItems
			.filter((item) => item.type === serviceType.GSM)
			.map((item) => item.legacyProductId);
		if (
			[idConfig.ROAMING, idConfig.INTERNATIONAL].every(
				(item) => _selectedOrderItems.indexOf(item.legacyProductId) >= 0
			)
		) {
			return translate('number.services.international.roaming');
		}
		if (_selectedOrderItems.indexOf(idConfig.INTERNATIONAL.legacyProductId) >= 0) {
			return translate('number.services.international');
		}
		return translate('none');
	}, [selectedOrderItems]);

	const mobileInternetServices = useMemo(() => {
		return (
			selectedOrderItems.find((item) => item.type === serviceType.MOBILE_INTERNET_PLANS)?.name ||
			translate('none')
		);
	}, [selectedOrderItems]);

	return (
		<div className={classNames(classes.summaryTab, classes.tabWrapper)}>
			<SummaryRow
				label={translate('organization.name')}
				value={account?.name || ''}
				icon={<Business />}
				className={classes.smallMargin}
				withBorder={false}
			/>
			<SummaryRow
				label={translate('billing.account')}
				value={selectedBillingAccount?.value}
				className={classes.mediumMargin}
			/>

			<SummaryRow label={translate('phoneNumber')} value={selectedNumber} />
			<SummaryRow
				label={translate('user.name')}
				value={selectedUser?.userName || ''}
				className={classes.mediumMargin}
			/>

			<SummaryRow label={translate('service.name')} value={translate('numbers.mobile-postpaid')} />
			<SummaryRow
				label={translate('tariff')}
				value={tariffPlan?.name || ''}
				className={classNames({
					[classes.mediumMargin]: !creditLimitPickerVisible,
				})}
			/>
			<SummaryRow
				label={translate('commitment')}
				value={
					commitmentDuration
						? translate(`new.postpaid.line.table.commitment.${commitmentDuration}`)
						: translate('new.postpaid.line.table.commitment.null')
				}
				className={classes.mediumMargin}
			/>
			<ConditionalRender show={!isRoamingInSelectedTariff}>
				<SummaryRow label={translate('services')} withBorder={false} className={classes.smallMargin} />
			</ConditionalRender>
			<ConditionalRender show={mobileInternetPlanPickerVisible && !isRoamingInSelectedTariff}>
				<SummaryRow label={translate('number.services.mobile.internet.plans')} value={mobileInternetServices} />
			</ConditionalRender>
			<ConditionalRender show={gsmServicesVisible && !isRoamingInSelectedTariff}>
				<SummaryRow label={translate('number.services.mobile.gsm.services')} value={gsmServices} />
			</ConditionalRender>
			<VatClause />
		</div>
	);
};

export default SummaryTab;
